import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'

export const MyPreset = definePreset(Aura, {
  semantic: {
    // Primary colors from Figma
    primary: {
      50: '#f3f0ff', // Very light indigo
      100: '#ede5ff', // Light indigo
      200: '#ddd0ff', // Lighter indigo
      300: '#c4b0ff', // Light-medium indigo
      400: '#a785ff', // Medium indigo
      500: '#23036A', // Primary Indigo (main) - Figma color
      600: '#1f0360', // Darker indigo
      700: '#1b0256', // Dark indigo
      800: '#17024c', // Very dark indigo
      900: '#130242', // Darkest indigo
      950: '#0f0238', // Ultra dark indigo
    },

    // Secondary colors from Figma
    secondary: {
      50: '#f0fffe', // Very light teal
      100: '#ccfffc', // Light teal
      200: '#99fff9', // Lighter teal
      300: '#5efff5', // Light-medium teal
      400: '#2dfff0', // Medium teal
      500: '#00B6A3', // Primary Teal (main) - Figma color
      600: '#00a392', // Darker teal
      700: '#009081', // Dark teal
      800: '#007d70', // Very dark teal
      900: '#006a5f', // Darkest teal
      950: '#00574e', // Ultra dark teal
    },

    // Define custom color tokens for your other Figma colors
    pink: {
      50: '#fff0f6',
      100: '#ffcce6',
      200: '#ff99cc',
      300: '#ff66b3',
      400: '#ff3399',
      500: '#FF0066', // Secondary Pink - Figma color
      600: '#e6005c',
      700: '#cc0052',
      800: '#b30047',
      900: '#99003d',
      950: '#800033',
    },

    yellow: {
      50: '#fffef0',
      100: '#fffacc',
      200: '#fff599',
      300: '#fff066',
      400: '#ffeb33',
      500: '#FFBC00', // Secondary Yellow - Figma color
      600: '#e6a900',
      700: '#cc9600',
      800: '#b38300',
      900: '#997000',
      950: '#805d00',
    },

    green: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#5FB375', // Secondary Medium Sea Green - Figma color
      600: '#207A12', // Secondary Tree Green - Figma color
      700: '#1c6b10',
      800: '#185c0e',
      900: '#144d0c',
      950: '#103e0a',
    },

    // Neutral colors for text and UI elements
    neutral: {
      50: '#fafafa', // Very light
      100: '#F5F5F5', // Light Grey Alternative - Figma color
      200: '#e5e5e5', // Light
      300: '#d7d7d7', // Light Grey with 40% opacity calculated
      400: '#9C9B9B', // Light Grey - Figma color
      500: '#737373', // Medium grey
      600: '#525252', //
      700: '#54565A', // Dark Grey - Figma color
      800: '#262626', // Very dark
      900: '#171717', // Darkest
      950: '#0a0a0a', // Ultra dark
    },

    // Color scheme specific configurations
    colorScheme: {
      light: {
        surface: {
          0: '#FFFFFF', // White background - Figma color
          50: '#fafafa',
          100: '#F5F5F5', // Light Grey Alternative - Figma color
          200: '#e5e5e5',
          300: '#d7d7d7', // Light Grey with 40% opacity calculated
          400: '#9C9B9B', // Light Grey - Figma color
          500: '#737373',
          600: '#525252',
          700: '#54565A', // Dark Grey - Figma color
          800: '#262626',
          900: '#171717',
          950: '#0a0a0a',
        },

        // Text colors
        text: {
          color: '#54565A', // Dark Grey for primary text - Figma color
          muted: '#9C9B9B', // Light Grey for secondary text - Figma color
          secondary: '#737373', // Medium grey for tertiary text
        },

        // Border colors
        border: {
          color: '{gray.200}', // Light Grey Alternative for borders - Figma color
        },

        // Form field specific colors
        formField: {
          borderColor: '#F5F5F5', // Light Grey Alternative - Figma color
          hoverBorderColor: '{primary.color}', // Primary color on hover
          focusBorderColor: '{primary.color}', // Primary color on focus
          background: '#FFFFFF', // White background - Figma color
          color: '#54565A', // Dark Grey text - Figma color
        },
      },
    },
  },

  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '{primary.500}',
              color: '#FFFFFF',
            },
            secondary: {
              background: '{primary.500}',
              color: '#FFFFFF',
            },
            label: {
              fontWeight: '600',
            },
          },
        },
      },
    },

    inputtext: {
      colorScheme: {
        light: {
          root: {
            background: '#FFFFFF',
            borderColor: '{border.color}',
            hoverBorderColor: '{formField.hoverBorderColor}',
            focusBorderColor: '{formField.focusBorderColor}',
            color: '{text.color}',
            shadow: 'none',
          },
        },
      },
    },

    textarea: {
      colorScheme: {
        light: {
          root: {
            background: '#FFFFFF',
            borderColor: '{border.color}',
            hoverBorderColor: '{formField.hoverBorderColor}',
            focusBorderColor: '{formField.focusBorderColor}',
            color: '{text.color}',
            shadow: 'none',
          },
        },
      },
    },

    select: {
      colorScheme: {
        light: {
          root: {
            background: '#FFFFFF',
            borderColor: '{border.color}',
            hoverBorderColor: '{formField.hoverBorderColor}',
            focusBorderColor: '{formField.focusBorderColor}',
            color: '{text.color}',
            shadow: 'none',
          },
        },
      },
    },

    rating: {
      colorScheme: {
        light: {
          icon: {
            size: '18px',
            activeColor: '{yellow.500}', // Active star color
          },
        },
      },
    },

    tooltip: {
      root: {
        maxWidth: '20rem', // Increased from default 12.5rem
        shadow: '0 2px 8px rgba(0, 0, 0, 0.1)', // Softer shadow
      },
      colorScheme: {
        light: {
          root: {
            background: '#FFFFFF', // White background
            color: '#000000',      // Black text
          },
        },
        dark: {
          root: {
            background: '#FFFFFF', // White background
            color: '#000000',      // Black text
          },
        },
      },
    },

    dialog: {
      footer: {
        padding: '1rem 1.5rem', // Add padding top and bottom to footer
      },
    },
  },
})
